import * as wjcCore from '@mescius/wijmo';
import * as wjcGrid from '@mescius/wijmo.grid';
import * as wjcGridXlsx from '@mescius/wijmo.grid.xlsx';
import * as wjInput from '@mescius/wijmo.input';
import * as wjOlap from '@mescius/wijmo.olap';

import {ViewChild, Component, AfterViewInit} from '@angular/core';
import {ToasterService} from 'angular2-toaster';
import {ActivatedRoute} from '@angular/router';
import {ActivePromotionConfig} from '../config/active-promotion.config';
import {asOrientDate, isOrientDate, DataService} from '@cosbeni/database';

/**
 * 買取屋 買取分析 (旧 kaitoriya.app /user `cj-user-analysis` の移植)。
 *
 * Wijmo OLAP (PivotEngine / PivotGrid / PivotPanel) による集計画面。3 モード:
 *  - 明細: AP_Order_Line を明細粒度で集計 (列=部門/最終区分/買取区分/売仕、行=買取開始日時、値=金額)。
 *  - ヘッダ: AP_Order_Head を伝票粒度で集計 (列=部門/最終区分/買取区分、行=買取開始日時、値=金額)。
 *  - 特殊: A〜D の 4 クエリを合成し大分類/中分類/小分類で問合せ・買取金額等を集計 (行=日付、値=値)。
 * 2 タブ目「元データ」は生成した行を FlexGrid で表示。Xlsx 出力あり。
 *
 * 統合アプリ移植時の差分:
 * - Wijmo は @mescius/*、設定は ActivePromotionConfig を使用。
 * - 旧「スプレッドシート反映」(gapi Google Sheets 書き出し / 環境依存の固定 spreadsheetId・列マッピング)
 *   は当アプリの gapi スコープ(drive のみ)に sheets が無く、移植対象外のため削除 (Xlsx 出力で代替)。
 */
@Component({
    standalone: false,
    selector: 'app-kaitoriya-analysis',
    templateUrl: 'analysis-management.component.html',
})
export class KaitoriyaAnalysisManagementComponent implements AfterViewInit {
    readonly: Boolean = true;
    @ViewChild('orderGrid') orderGrid: wjcGrid.FlexGrid;
    @ViewChild('pivotGrid', {static: true}) pivotGrid: wjOlap.PivotGrid;
    @ViewChild('fromDateInput') fromDateInput: wjInput.InputDate;
    @ViewChild('toDateInput') toDateInput: wjInput.InputDate;
    fromDate: any;
    toDate: any;
    keyword: string = '';
    message: string = '';
    mode: string = '明細';
    report: string = 'お問い合わせ実績';
    screenWidth: number;
    orders: wjcCore.CollectionView = new wjcCore.CollectionView([]);
    pivot: wjOlap.PivotEngine;

    constructor(public dataService: DataService, public toasterService: ToasterService, private route: ActivatedRoute) {
        var today = new Date();
        this.fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
        this.toDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    }

    ngAfterViewInit() {
    }

    search(): void {
        if (this.mode == '明細') {
            let where = [];
            if (!wjcCore.isNullOrWhiteSpace(this.keyword)) {
                where.push('header.name LIKE \'%' + this.keyword + '%\'');
                where.push('header.status LIKE \'%' + this.keyword + '%\'');
                where.push('header.finalStatus LIKE \'%' + this.keyword + '%\'');
                where.push('header.state LIKE \'%' + this.keyword + '%\'');
                where.push('header.city LIKE \'%' + this.keyword + '%\'');
                where.push('productName LIKE \'%' + this.keyword + '%\'');
                where.push('manufactureName LIKE \'%' + this.keyword + '%\'');
                where.push('modelNumber LIKE \'%' + this.keyword + '%\'');
            }
            if (wjcCore.isDate(this.fromDate) && wjcCore.isDate(this.toDate)) {
                where.push('(header.orderDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\' AND header.orderDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\')');
                where.push('(header.startDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\' AND header.startDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\')');
                where.push('(header.endDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\' AND header.endDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\')');
            } else {
                if (wjcCore.isDate(this.fromDate)) {
                    where.push('header.orderDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\'');
                    where.push('header.startDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\'');
                    where.push('header.endDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\'');
                }
                if (wjcCore.isDate(this.toDate)) {
                    where.push('header.orderDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\'');
                    where.push('header.startDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\'');
                    where.push('header.endDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\'');
                }
            }
            this.dataService.query({
                sql: 'SELECT header.id as id,header.branch as branch,header.name as name,header.status as status,header.finalStatus as finalStatus,header.department as department,header.staff as staff,header.estimateStaff as estimateStaff,header.state as state,header.city as city ,header.orderDate as orderDate,header.startDate as startDate,header.endDate as endDate,productName,manufactureName,modelNumber,quantity,price,amount FROM AP_Order_Line WHERE ' + where.join(' OR '),
                count: 'SELECT header.id as id,header.branch as branch,header.name as name,header.status as status,header.finalStatus as finalStatus,header.department as department,header.staff as staff,header.estimateStaff as estimateStaff,header.state as state,header.city as city ,header.orderDate as orderDate,header.startDate as startDate,header.endDate as endDate,productName,manufactureName,modelNumber,quantity,price,amount FROM AP_Order_Line WHERE ' + where.join(' OR '),
                fetchPlan: '*:1',
            }, ActivePromotionConfig.SERVER_URL).subscribe(data => {
                for (let item of data.results) {
                    if (isOrientDate(item.orderDate)) {
                        item.orderDate = asOrientDate(item.orderDate);
                    }
                    if (isOrientDate(item.startDate)) {
                        item.startDate = asOrientDate(item.startDate);
                    }
                    if (isOrientDate(item.endDate)) {
                        item.endDate = asOrientDate(item.endDate);
                    }
                }
                let orders = [];
                for (let item of data.results) {
                    let order = {
                        '番号': item.id,
                        '枝番': item.branch,
                        '名前': item.name,
                        '買取区分': item.status,
                        '最終区分': item.finalStatus,
                        '部門': item.department,
                        '担当者': item.staff,
                        '見積担当者': item.estimateStaff,
                        '都道府県': item.state,
                        '市区町村': item.city,
                        '受付日': item.orderDate,
                        '買取開始日時': item.startDate,
                        '買取終了日時': item.endDate,
                        '商品名': item.productName,
                        'メーカー': item.manufactureName,
                        '型番': item.modelNumber,
                        '数量': item.quantity,
                        '単価': item.price,
                        '金額': item.amount,
                        '売仕': '仕入'
                    };
                    if (item.amount < 0) {
                        order['売仕'] = '売上';
                    }
                    orders.push(order);
                }
                this.orders = new wjcCore.CollectionView(wjcCore.asArray(orders));
                this.pivot = new wjOlap.PivotEngine(
                    {
                        autoGenerateFields: false,
                        itemsSource: this.orders,
                        showRowTotals: wjOlap.ShowTotals.Subtotals,
                        fields: [
                            {binding: '番号', header: '番号'},
                            {binding: '枝番', header: '枝番'},
                            {binding: '名前', header: '名前'},
                            {binding: '買取区分', header: '買取区分'},
                            {binding: '最終区分', header: '最終区分'},
                            {binding: '部門', header: '部門'},
                            {binding: '担当者', header: '担当者'},
                            {binding: '見積担当者', header: '見積担当者'},
                            {binding: '都道府県', header: '都道府県'},
                            {binding: '市区町村', header: '市区町村'},
                            {binding: '受付日', header: '受付日'},
                            {binding: '買取開始日時', header: '買取開始日時'},
                            {binding: '買取終了日時', header: '買取終了日時'},
                            {binding: '商品名', header: '商品名'},
                            {binding: 'メーカー', header: 'メーカー'},
                            {binding: '型番', header: '型番'},
                            {binding: '数量', header: '数量', aggregate: 'Sum'},
                            {binding: '単価', header: '単価'},
                            {binding: '金額', header: '金額', aggregate: 'Sum'},
                            {binding: '売仕', header: '売仕'},
                        ],
                        columnFields: ['部門', '最終区分', '買取区分', '売仕'],
                        rowFields: ['買取開始日時'],
                        valueFields: ['金額']
                    }
                );
            });
        } else if (this.mode == '特殊') {
            this.orders = new wjcCore.CollectionView([]);
            let whereA = ['header.finalStatus=\'完了\''];
            let whereB = [];
            let whereC = [];
            let whereD = ['header.finalStatus=\'完了\' AND (header.department=\'海外\' OR header.department=\'海外（九州）\')'];
            if (wjcCore.isDate(this.fromDate) && wjcCore.isDate(this.toDate)) {
                whereA.push('(header.startDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\' AND header.startDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\')');
                whereB.push('(startDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\' AND startDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\')');
                whereC.push('(orderDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\' AND orderDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\')');
                whereD.push('(header.orderDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\' AND header.orderDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\')');
            } else {
                if (wjcCore.isDate(this.fromDate)) {
                    whereA.push('header.startDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\'');
                    whereB.push('startDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\'');
                    whereC.push('orderDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\'');
                    whereD.push('header.orderDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\'');
                }
                if (wjcCore.isDate(this.toDate)) {
                    whereA.push('header.startDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\'');
                    whereB.push('startDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\'');
                    whereC.push('orderDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\'');
                    whereD.push('header.orderDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\'');
                }
            }
            let queryA = 'SELECT header.id as id,header.branch as branch,header.name as name,header.status as status,header.finalStatus as finalStatus,header.department as department,header.staff as staff,header.state as state,header.city as city ,header.orderDate as orderDate,header.startDate as startDate,header.endDate as endDate,productName,manufactureName,modelNumber,oversea,quantity,price,amount FROM AP_Order_Line WHERE ' + whereA.join(' AND ');
            let queryB = 'SELECT id,branch,name,status,finalStatus,department,staff,state,city,orderDate,startDate,endDate,total FROM AP_Order_Head WHERE ' + whereB.join(' AND ');
            let queryC = 'SELECT id,branch,name,status,finalStatus,department,staff,state,city,orderDate,startDate,endDate,total FROM AP_Order_Head WHERE ' + whereC.join(' AND ') + ' ORDER BY startDate DESC';
            let queryD = 'SELECT header.id as id,header.branch as branch,header.name as name,header.status as status,header.finalStatus as finalStatus,header.department as department,header.staff as staff,header.state as state,header.city as city ,header.orderDate as orderDate,header.startDate as startDate,header.endDate as endDate,productName,manufactureName,modelNumber,oversea,quantity,price,amount FROM AP_Order_Line WHERE ' + whereD.join(' AND ');
            // 4 クエリの前にピボットを構築しておく (各コールバックで itemsSource を張り替える)。
            this.pivot = new wjOlap.PivotEngine(
                {
                    autoGenerateFields: false,
                    itemsSource: this.orders,
                    showColumnTotals: wjOlap.ShowTotals.None,
                    showRowTotals: wjOlap.ShowTotals.Subtotals,
                    fields: [
                        {binding: '番号', header: '番号'},
                        {binding: '枝番', header: '枝番'},
                        {binding: '名前', header: '名前'},
                        {binding: '買取区分', header: '買取区分'},
                        {binding: '最終区分', header: '最終区分'},
                        {binding: '部門', header: '部門'},
                        {binding: '担当者', header: '担当者'},
                        {binding: '都道府県', header: '都道府県'},
                        {binding: '受付日', header: '受付日'},
                        {binding: '買取開始日時', header: '買取開始日時'},
                        {binding: '買取終了日時', header: '買取終了日時'},
                        {binding: '商品名', header: '商品名'},
                        {binding: 'メーカー', header: 'メーカー'},
                        {binding: '型番', header: '型番'},
                        {binding: '数量', header: '数量', aggregate: 'Sum'},
                        {binding: '単価', header: '単価'},
                        {binding: '金額', header: '金額', aggregate: 'Sum'},
                        {binding: '売仕', header: '売仕'},
                        {binding: '日付', header: '日付'},
                        {binding: '大分類', header: '大分類'},
                        {binding: '中分類', header: '中分類'},
                        {binding: '小分類', header: '小分類'},
                        {binding: '値', header: '値', aggregate: 'Sum'},
                    ],
                    columnFields: ['大分類', '中分類', '小分類'],
                    rowFields: ['日付'],
                    valueFields: ['値']
                }
            );
            this.dataService.query({sql: queryA, count: queryA, fetchPlan: '*:1'}, ActivePromotionConfig.SERVER_URL).subscribe(data => {
                for (let item of data.results) {
                    if (isOrientDate(item.orderDate)) {
                        item.orderDate = asOrientDate(item.orderDate);
                    }
                    if (isOrientDate(item.startDate)) {
                        item.startDate = asOrientDate(item.startDate);
                    }
                    if (isOrientDate(item.endDate)) {
                        item.endDate = asOrientDate(item.endDate);
                    }
                }
                for (let item of data.results) {
                    let order = {
                        '番号': item.id, '枝番': item.branch, '名前': item.name, '買取区分': item.status,
                        '最終区分': item.finalStatus, '部門': item.department, '担当者': item.staff,
                        '都道府県': item.state, '市区町村': item.city, '受付日': item.orderDate,
                        '買取開始日時': item.startDate, '買取終了日時': item.endDate, '商品名': item.productName,
                        'メーカー': item.manufactureName, '型番': item.modelNumber, '数量': item.quantity,
                        '単価': item.price, '金額': item.amount, '売仕': '仕入',
                        '日付': item.startDate, '大分類': item.department, '中分類': '3 買取金額',
                        '小分類': item.oversea, '値': item.amount,
                    };
                    if (item.amount < 0) {
                        order['売仕'] = '売上';
                    }
                    if (item.amount < 0) {
                        order['中分類'] = '4 回収売上';
                        order['値'] = order['値'] * -1;
                    }
                    if (item.department.indexOf('海外') >= 0) {
                        continue;
                    }
                    if (item.department.indexOf('海外') < 0 && item.oversea == 'BB仕入') {
                        order['大分類'] = '海外';
                        order['中分類'] = '5 キャリー';
                        order['小分類'] = '';
                    }
                    this.orders.sourceCollection.push(order);
                }
                this.orders.refresh();
                this.pivot.itemsSource = this.orders;
            });
            this.dataService.query({sql: queryB, count: queryB, fetchPlan: '*:1'}, ActivePromotionConfig.SERVER_URL).subscribe(data => {
                let orderIds = {};
                for (let item of data.results) {
                    if (isOrientDate(item.orderDate)) {
                        item.orderDate = asOrientDate(item.orderDate);
                    }
                    if (isOrientDate(item.startDate)) {
                        item.startDate = asOrientDate(item.startDate);
                    }
                    if (isOrientDate(item.endDate)) {
                        item.endDate = asOrientDate(item.endDate);
                    }
                }
                for (let item of data.results) {
                    let order = {
                        '番号': item.id, '枝番': item.branch, '名前': item.name, '買取区分': item.status,
                        '最終区分': item.finalStatus, '部門': item.department, '担当者': item.staff,
                        '都道府県': item.state, '受付日': item.orderDate, '買取開始日時': item.startDate,
                        '買取終了日時': item.endDate, '商品名': '', 'メーカー': '', '型番': '', '数量': 1,
                        '単価': item.total, '金額': item.total, '売仕': '仕入', '日付': item.startDate,
                        '大分類': item.department, '中分類': '1 見積', '小分類': '', '値': 1,
                    };
                    if (orderIds.hasOwnProperty(item.id) && orderIds[item.id].hasOwnProperty(wjcCore.Globalize.formatDate(item.startDate, 'yyyyMMdd'))) {
                        order['値'] = 0;
                    } else {
                        if (!orderIds.hasOwnProperty(item.id)) {
                            orderIds[item.id] = {};
                        }
                        orderIds[item.id][wjcCore.Globalize.formatDate(item.startDate, 'yyyyMMdd')] = 1;
                    }
                    if (item.amount < 0) {
                        order['売仕'] = '売上';
                    }
                    if (item.finalStatus != 'キャンセル' && item.status != 'ｷｬﾝｾﾙ') {
                        if (item.department == 'キャリー') {
                            if (item.status == '見積' || item.status == '見積即' || item.status.indexOf('LINE') >= 0) {
                                this.orders.sourceCollection.push(order);
                            }
                        } else {
                            this.orders.sourceCollection.push(order);
                        }
                    }
                    if (item.finalStatus == '完了') {
                        let orderSuccess = {...order};
                        orderSuccess['中分類'] = '2 成約';
                        this.orders.sourceCollection.push(orderSuccess);
                    }
                }
                this.orders.refresh();
                this.pivot.itemsSource = this.orders;
            });
            this.dataService.query({sql: queryC, count: queryC, fetchPlan: '*:1'}, ActivePromotionConfig.SERVER_URL).subscribe(data => {
                let orderIds = {};
                for (let item of data.results) {
                    if (isOrientDate(item.orderDate)) {
                        item.orderDate = asOrientDate(item.orderDate);
                    }
                    if (isOrientDate(item.startDate)) {
                        item.startDate = asOrientDate(item.startDate);
                    }
                    if (isOrientDate(item.endDate)) {
                        item.endDate = asOrientDate(item.endDate);
                    }
                }
                for (let item of data.results) {
                    if (item.status.indexOf('見積') >= 0 || item.status.indexOf('トレ電話') >= 0 || item.status.indexOf('LINE') >= 0) {
                        let order = {
                            '番号': item.id, '枝番': item.branch, '名前': item.name, '買取区分': item.status,
                            '最終区分': item.finalStatus, '部門': item.department, '担当者': item.staff,
                            '都道府県': item.state, '受付日': item.orderDate, '買取開始日時': item.startDate,
                            '買取終了日時': item.endDate, '商品名': '', 'メーカー': '', '型番': '', '数量': 1,
                            '単価': item.total, '金額': item.total, '売仕': '仕入', '日付': item.orderDate,
                            '大分類': '1 問合せ', '中分類': item.state, '小分類': 'TEL+mail', '値': 1,
                        };
                        if (orderIds.hasOwnProperty(item.id)) {
                            order['値'] = 0;
                        } else {
                            orderIds[item.id] = 1;
                        }
                        if (item.amount < 0) {
                            order['売仕'] = '売上';
                        }
                        if (item.state.indexOf('兵庫') >= 0) {
                            order['中分類'] = '兵庫';
                        }
                        if (item.state.indexOf('大阪') >= 0) {
                            order['中分類'] = '大阪';
                        }
                        if (item.status.indexOf('トレ電話') >= 0) {
                            order['中分類'] = 'トレ';
                        }
                        if (item.status.indexOf('LINE') >= 0) {
                            order['中分類'] = 'LINE';
                        }
                        if (order['中分類'] != '兵庫' && order['中分類'] != '大阪' && order['中分類'] != 'トレ' && order['中分類'] != 'LINE') {
                            order['中分類'] = 'その他';
                        }
                        this.orders.sourceCollection.push(order);
                        if (!wjcCore.isNullOrWhiteSpace(item.startDate)) {
                            let orderSuccess = {...order};
                            orderSuccess['小分類'] = '決定';
                            this.orders.sourceCollection.push(orderSuccess);
                        }
                    }
                }
                this.orders.refresh();
                this.pivot.itemsSource = this.orders;
            });
            this.dataService.query({sql: queryD, count: queryD, fetchPlan: '*:1'}, ActivePromotionConfig.SERVER_URL).subscribe(data => {
                for (let item of data.results) {
                    if (isOrientDate(item.orderDate)) {
                        item.orderDate = asOrientDate(item.orderDate);
                    }
                    if (isOrientDate(item.startDate)) {
                        item.startDate = asOrientDate(item.startDate);
                    }
                    if (isOrientDate(item.endDate)) {
                        item.endDate = asOrientDate(item.endDate);
                    }
                }
                for (let item of data.results) {
                    let order = {
                        '番号': item.id, '枝番': item.branch, '名前': item.name, '買取区分': item.status,
                        '最終区分': item.finalStatus, '部門': item.department, '担当者': item.staff,
                        '都道府県': item.state, '市区町村': item.city, '受付日': item.orderDate,
                        '買取開始日時': item.startDate, '買取終了日時': item.endDate, '商品名': item.productName,
                        'メーカー': item.manufactureName, '型番': item.modelNumber, '数量': item.quantity,
                        '単価': item.price, '金額': item.amount, '売仕': '仕入', '日付': item.orderDate,
                        '大分類': item.department, '中分類': '3 買取金額', '小分類': item.oversea, '値': item.amount,
                    };
                    if (item.amount < 0) {
                        order['売仕'] = '売上';
                    }
                    if (item.amount < 0) {
                        order['中分類'] = '4 回収売上';
                        order['値'] = order['値'] * -1;
                    }
                    if (item.department.indexOf('海外') >= 0) {
                        order['大分類'] = '海外';
                        if (item.department == '海外') {
                            order['中分類'] = '3 BB仕入';
                        } else if (item.department == '海外（九州）') {
                            order['中分類'] = '4 BB仕入:九州';
                        }
                    }
                    if (item.department.indexOf('海外') < 0 && item.oversea == 'BB仕入') {
                        order['大分類'] = '海外';
                        order['中分類'] = '5 キャリー';
                        order['小分類'] = '';
                    }
                    this.orders.sourceCollection.push(order);
                }
                this.orders.refresh();
                this.pivot.itemsSource = this.orders;
            });
        } else {
            let where = [];
            if (wjcCore.isDate(this.fromDate) && wjcCore.isDate(this.toDate)) {
                where.push('(orderDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\' AND orderDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\')');
                where.push('(startDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\' AND startDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\')');
                where.push('(endDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\' AND endDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\')');
            } else {
                if (wjcCore.isDate(this.fromDate)) {
                    where.push('orderDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\'');
                    where.push('startDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\'');
                    where.push('endDate >= \'' + wjcCore.Globalize.formatDate(this.fromDate, 'yyyy-MM-dd HH:mm:ss') + '\'');
                }
                if (wjcCore.isDate(this.toDate)) {
                    where.push('orderDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\'');
                    where.push('startDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\'');
                    where.push('endDate < \'' + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(this.toDate, 1), 'yyyy-MM-dd HH:mm:ss') + '\'');
                }
            }
            this.dataService.query({
                sql: 'SELECT id,branch,name,status,finalStatus,department,staff,estimateStaff,state,city,orderDate,startDate,endDate,total FROM AP_Order_Head WHERE ' + where.join(' OR '),
                count: 'SELECT id,branch,name,status,finalStatus,department,staff,estimateStaff,state,city,orderDate,startDate,endDate,total FROM AP_Order_Head WHERE ' + where.join(' OR '),
                fetchPlan: '*:1',
            }, ActivePromotionConfig.SERVER_URL).subscribe(data => {
                for (let item of data.results) {
                    if (isOrientDate(item.orderDate)) {
                        item.orderDate = asOrientDate(item.orderDate);
                    }
                    if (isOrientDate(item.startDate)) {
                        item.startDate = asOrientDate(item.startDate);
                    }
                    if (isOrientDate(item.endDate)) {
                        item.endDate = asOrientDate(item.endDate);
                    }
                }
                let orders = [];
                for (let item of data.results) {
                    let order = {
                        '番号': item.id, '枝番': item.branch, '名前': item.name, '買取区分': item.status,
                        '最終区分': item.finalStatus, '部門': item.department, '担当者': item.staff,
                        '見積担当者': item.estimateStaff, '都道府県': item.state, '受付日': item.orderDate,
                        '買取開始日時': item.startDate, '買取終了日時': item.endDate, '商品名': '', 'メーカー': '',
                        '型番': '', '数量': 1, '単価': item.total, '金額': item.total, '売仕': '仕入'
                    };
                    if (item.amount < 0) {
                        order['売仕'] = '売上';
                    }
                    orders.push(order);
                }
                this.orders = new wjcCore.CollectionView(wjcCore.asArray(orders));
                this.pivot = new wjOlap.PivotEngine(
                    {
                        autoGenerateFields: false,
                        itemsSource: this.orders,
                        showRowTotals: wjOlap.ShowTotals.Subtotals,
                        fields: [
                            {binding: '番号', header: '番号'},
                            {binding: '枝番', header: '枝番'},
                            {binding: '名前', header: '名前'},
                            {binding: '買取区分', header: '買取区分'},
                            {binding: '最終区分', header: '最終区分'},
                            {binding: '部門', header: '部門'},
                            {binding: '担当者', header: '担当者'},
                            {binding: '見積担当者', header: '見積担当者'},
                            {binding: '都道府県', header: '都道府県'},
                            {binding: '受付日', header: '受付日'},
                            {binding: '買取開始日時', header: '買取開始日時'},
                            {binding: '買取終了日時', header: '買取終了日時'},
                            {binding: '商品名', header: '商品名'},
                            {binding: 'メーカー', header: 'メーカー'},
                            {binding: '型番', header: '型番'},
                            {binding: '数量', header: '数量', aggregate: 'Sum'},
                            {binding: '単価', header: '単価'},
                            {binding: '金額', header: '金額', aggregate: 'Sum'},
                            {binding: '売仕', header: '売仕'},
                        ],
                        columnFields: ['部門', '最終区分', '買取区分'],
                        rowFields: ['買取開始日時'],
                        valueFields: ['金額']
                    }
                );
            });
        }
    }

    onChangeFromDate(): void {
        this.toDateInput.min = this.fromDate;
    }

    export(grid: string, name: string) {
        wjcGridXlsx.FlexGridXlsxConverter.saveAsync(this[grid], {
            includeColumnHeaders: true,
            includeRowHeaders: true,
            includeCellStyles: false
        }, name + '.xlsx');
    }
}
